import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

// @mui
import { CssBaseline } from '@mui/material';
import {
  alpha,
  createTheme,
  ThemeProvider as MUIThemeProvider,
} from '@mui/material/styles';
//
import palette from './palette';
import typography from './typography';
import breakpoints from './breakpoints';
import componentsOverride from './overrides';
import shadows, { customShadows } from './shadows';
import getColorPresets from '../utils/getColorPresets';

import useLocales from '@/hooks/useLocales';
import { useColorContext } from './colorContext';

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default function ThemeProvider({ children }) {
  const setColor = getColorPresets('blue');
  const { currentLang } = useLocales();
  const { mode } = useColorContext();
  const colorBlue = getColorPresets('blue');
  const colorOrange = getColorPresets('orange');

  const themeLightOptions = {
    palette: {
      ...palette.light,
      primary: colorBlue,
      secondary: colorOrange,
    },
    typography,
    breakpoints,
    shape: { borderRadius: 8 },
    direction: 'ltr',
    shadows: shadows.light,
    customShadows: {
      ...customShadows.light,
      primary: `0 8px 16px 0 ${alpha(setColor.main, 0.24)}`,
    },
  };

  const themeDarkOptions = {
    palette: {
      ...palette.dark,
      primary: {
        ...colorBlue,
        main: colorBlue.palette[200],
        light: colorBlue.palette[100],
        dark: colorBlue.palette[400],
      },
      secondary: {
        ...colorOrange,
        main: colorOrange.palette[300],
        light: colorOrange.palette[200],
        dark: colorOrange.palette[400],
      },
    },
    typography,
    breakpoints,
    shape: { borderRadius: 8 },
    direction: 'ltr',
    shadows: shadows.dark,
    customShadows: {
      ...customShadows.dark,
      primary: `0 8px 16px 0 ${alpha(setColor.main, 0.24)}`,
    },
  };

  const theme = createTheme(
    mode === 'dark' ? themeDarkOptions : themeLightOptions,
    currentLang.systemValue,
  );

  theme.components = componentsOverride(theme);

  return (
    <MUIThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MUIThemeProvider>
  );
}
